import type {
  CmmdProviderAccountInventoryAccount,
  ThreadProviderRateLimitStatus,
} from "@cmmd-forge/contracts";

import type { ProviderUsageAccountView } from "~/usage/useProviderUsage";
import { findProviderAccountByIdentity } from "@cmmd-forge/shared/providerAccountIdentity";

export interface RateLimitBannerWindow {
  readonly status: "allowed" | "allowed_warning" | "rejected";
  readonly usedPercent: number;
  readonly resetsAt: string;
}

function rank(window: RateLimitBannerWindow): number {
  return window.status === "rejected" ? 0 : 1;
}

/** Contract providers are "claude"/"codex"; ModelSelection uses "claudeAgent". */
function toStatusProvider(provider: "codex" | "claudeAgent"): "claude" | "codex" {
  return provider === "claudeAgent" ? "claude" : "codex";
}

/**
 * An absent `modelSelection.providerAccountId` means "whatever account is
 * default", not "no account" — threads created before account selection existed
 * carry no id but still run on the default account. Resolving it matters twice:
 * telemetry stamped with the default account's id must still match, and the
 * default account must not be offered as the account to switch TO.
 */
export function resolveEffectiveAccountId(input: {
  readonly provider: "codex" | "claudeAgent";
  readonly currentProviderAccountId: string | undefined;
  readonly accounts: ReadonlyArray<CmmdProviderAccountInventoryAccount> | undefined;
}): string | null {
  const providerAccounts = input.accounts?.filter((account) => account.provider === input.provider);
  if (input.currentProviderAccountId !== undefined) {
    return (
      findProviderAccountByIdentity(providerAccounts ?? [], input.currentProviderAccountId)
        ?.providerAccountId ?? input.currentProviderAccountId
    );
  }
  const fallback = providerAccounts?.find((account) => account.isDefault);
  return fallback?.providerAccountId ?? null;
}

function resolveKnownProviderAccountId(
  accounts: ReadonlyArray<CmmdProviderAccountInventoryAccount> | undefined,
  provider: "codex" | "claudeAgent",
  providerAccountId: string,
): string {
  const providerAccounts = accounts?.filter((account) => account.provider === provider) ?? [];
  return (
    findProviderAccountByIdentity(providerAccounts, providerAccountId)?.providerAccountId ??
    providerAccountId
  );
}

/**
 * Picks the window the banner should describe, or `undefined` for silence.
 *
 * Three rules, each closing a way this misreported:
 *
 * 1. ACCOUNT SCOPE — telemetry is matched to the account it was reported for.
 *    Matching on provider alone meant switching from A to B immediately
 *    re-attributed A's rejection to B. Entries with no recorded account
 *    (pre-dating the field) still match, so old rows do not go silent.
 * 2. EXPIRY — a window whose `resetsAt` has passed is no longer actionable.
 *    No further provider event is guaranteed once the user stops sending
 *    turns, so without this a session sits on "rate limit reached, resets in
 *    under a minute" indefinitely and keeps pushing an unnecessary switch.
 * 3. REJECTED FIRST — a rejected window outranks a warning regardless of
 *    utilisation. Sorting on usedPercent alone let a 90%-used WARNING outrank
 *    an 80%-used REJECTION, so the banner said "usage running high" while
 *    requests were already being refused.
 */
export function selectRateLimitBannerWindow(input: {
  readonly provider: "codex" | "claudeAgent";
  readonly effectiveAccountId: string | null;
  readonly accounts?: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly rateLimitStatus: ReadonlyArray<ThreadProviderRateLimitStatus> | undefined;
  readonly nowMs: number;
}): RateLimitBannerWindow | undefined {
  const statusProvider = toStatusProvider(input.provider);
  const entry = input.rateLimitStatus?.find((candidate) => {
    if (candidate.provider !== statusProvider) return false;
    const reportedAccountId = candidate.providerAccountId ?? null;
    return (
      reportedAccountId === null ||
      resolveKnownProviderAccountId(input.accounts, input.provider, reportedAccountId) ===
        input.effectiveAccountId
    );
  });
  if (!entry) return undefined;

  return [entry.fiveHour, entry.sevenDay]
    .filter((window): window is RateLimitBannerWindow => window !== null)
    .filter((window) => window.status !== "allowed")
    .filter((window) => {
      const resetsAtMs = new Date(window.resetsAt).getTime();
      // An unparseable reset time is kept: dropping it would silently hide a
      // real rejection because of a formatting problem.
      return Number.isNaN(resetsAtMs) || resetsAtMs > input.nowMs;
    })
    .toSorted((a, b) => {
      return rank(a) - rank(b) || b.usedPercent - a.usedPercent;
    })[0];
}

/**
 * The soonest reset among actionable windows, so the caller can re-evaluate
 * exactly when one expires instead of polling.
 */
export function nextRateLimitResetMs(input: {
  readonly window: RateLimitBannerWindow | undefined;
  readonly nowMs: number;
}): number | null {
  if (!input.window) return null;
  const resetsAtMs = new Date(input.window.resetsAt).getTime();
  if (Number.isNaN(resetsAtMs) || resetsAtMs <= input.nowMs) return null;
  return resetsAtMs - input.nowMs;
}

/**
 * FOR-32770: a switch is only a way out when the other account actually has
 * headroom. If its usage snapshot reports full windows, offering the switch
 * would move the user nowhere; unknown data (no snapshot) keeps the offer,
 * because the old behavior is better than suppressing a working escape hatch
 * on absent telemetry.
 */
function otherAccountHasHeadroom(snapshot: ProviderUsageAccountView["snapshot"]): boolean {
  if (!snapshot) return true;
  const windows = [snapshot.fiveHour, snapshot.sevenDay];
  const reported = windows.filter((window) => window !== null);
  if (reported.length === 0) return true;
  return reported.every((window) => window.usedPercent < 100 && window.status !== "rejected");
}

/**
 * Codex-only switch target: another linked account that still has window
 * headroom. ClaudeAdapter.ts does not read ModelSelection.providerAccountId,
 * so a Claude switch would silently change nothing - undefined, always.
 */
export function resolveSwitchTargetWithHeadroom(input: {
  readonly provider: "codex" | "claudeAgent";
  readonly effectiveAccountId: string | null;
  readonly accounts: ReadonlyArray<CmmdProviderAccountInventoryAccount> | undefined;
  readonly usageAccounts: ReadonlyArray<ProviderUsageAccountView>;
}): CmmdProviderAccountInventoryAccount | undefined {
  if (input.provider !== "codex") return undefined;
  return input.accounts?.find((account) => {
    if (
      account.provider !== input.provider ||
      account.providerAccountId === input.effectiveAccountId ||
      account.needsReconnect
    )
      return false;
    const snapshot =
      input.usageAccounts.find(
        (entry) =>
          entry.account.provider === input.provider &&
          entry.account.providerAccountId === account.providerAccountId,
      )?.snapshot ?? null;
    return otherAccountHasHeadroom(snapshot);
  });
}
